// Досье сотрудника C (RD-149): view model, нормализация входных DTO и сборка write payload.
// User ≠ Employment: user routes принимают User UUID, employments/статистика — Employment UUID.
import dayjs, { type Dayjs } from 'dayjs'
import 'dayjs/locale/ru'
import type { Option, Schedule, Shift } from '@/types'
import { formatEmploymentDateForApi } from '@/utils/employmentDate'

export type DossierContext = 'schedule' | 'employees'
export type DossierTab = 'summary' | 'shifts' | 'discipline' | 'exchange'
// personal — «Личные данные» со списком ТУ; employment/new-employment — подэкраны ТУ поверх неё (вариант B r2).
export type DossierScreen = 'dossier' | 'personal' | 'employment' | 'new-employment' | 'password' | 'responses'
export type EditFocus = 'user' | 'employment' | 'smart'
// Дисциплина: месяц / последние 3 календарных месяца включая текущий / год с 1 января.
export type DisciplinePeriod = 'month' | '3m' | 'year'

export type BlockStatus = 'loading' | 'ready' | 'error' | 'forbidden'

export interface Block<T> {
    status: BlockStatus
    data: T | null
}

export interface DossierPerson {
    id: string
    firstName: string
    lastName: string
    middleName: string | null
    phone: string | null
    email: string | null
    avatar: string | null
    // null — значение ещё не известно (строка списка его не содержит).
    smartCheckin: boolean | null
    // User.public_api_fields (RD-154): поля, закреплённые интеграцией. null — массив не получен/невалиден:
    // не подменяется [], запись User до актуализации недоступна.
    publicApiFields: string[] | null
}

export interface DossierEmployment {
    id: string
    departmentId: string | null
    departmentName: string | null
    positionId: string | null
    positionName: string | null
    hiredAt: string | null
    firedAt: string | null
    // Доля 0..1; null — неизвестна.
    percentage: number | null
    shiftScheduleId: string | null
    salaryHour: number | null
    tabelCode: string | null
    // false — запись известна только по строке списка (нет fired_at и прочих полей).
    // is_main из EmploymentForUser; false для записи строки списка/графика.
    isMain: boolean
    complete: boolean
    // Employment.public_api_fields (RD-161), отдельно от User. null — не получен: запись ТУ недоступна.
    publicApiFields: string[] | null
}

interface NameDirectories {
    departments: Option[]
    positions: Option[]
}

const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value : null)
const numberOrNull = (value: unknown) => {
    if (value === null || value === undefined || value === '') return null
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
}
const idOf = (value: unknown): string | null => {
    if (typeof value === 'string' && value) return value
    if (value && typeof value === 'object' && typeof (value as { id?: unknown }).id === 'string') return (value as { id: string }).id
    return null
}
const nameOf = (value: unknown): string | null => (
    value && typeof value === 'object' ? text((value as { name?: unknown }).name) : null
)
// public_api_fields: read-only string[]; иначе null (не []).
export const publicApiFieldsOf = (value: unknown): string[] | null => (
    Array.isArray(value) && value.every(item => typeof item === 'string') ? [...value] as string[] : null
)
const findName = (options: Option[], id: string | null) => (id ? options.find(item => item.id === id)?.name ?? null : null)

export function normalizePerson(source: Record<string, unknown> | null | undefined): DossierPerson {
    const value = source ?? {}
    return {
        id: String(value.id ?? ''),
        firstName: typeof value.first_name === 'string' ? value.first_name : '',
        lastName: typeof value.last_name === 'string' ? value.last_name : '',
        middleName: text(value.middle_name),
        phone: text(value.phone),
        email: text(value.email),
        avatar: text(value.avatar),
        smartCheckin: typeof value.smart_checkin_possible === 'boolean' ? value.smart_checkin_possible : null,
        publicApiFields: publicApiFieldsOf(value.public_api_fields),
    }
}

// EmploymentForUser (GET /api/user/{id}/) или EmployeeRowEmployment (строка списка, без fired_at).
export function normalizeUserEmployment(source: Record<string, unknown>, directories: NameDirectories, complete: boolean): DossierEmployment {
    const departmentId = idOf(source.department)
    const positionId = idOf(source.position)
    return {
        id: String(source.id ?? ''),
        departmentId,
        departmentName: nameOf(source.department) ?? findName(directories.departments, departmentId),
        positionId,
        positionName: nameOf(source.position) ?? findName(directories.positions, positionId),
        hiredAt: text(source.hired_at),
        firedAt: text(source.fired_at),
        percentage: numberOrNull(source.percentage),
        shiftScheduleId: idOf(source.shift_schedule),
        salaryHour: numberOrNull(source.salary_hour),
        tabelCode: text(source.employee_tabel_code),
        isMain: source.is_main === true,
        complete,
        publicApiFields: publicApiFieldsOf(source.public_api_fields),
    }
}

export function normalizeUserEmployments(source: unknown, directories: NameDirectories, complete: boolean) {
    if (!Array.isArray(source)) return []
    return source
        .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object' && (item as { id?: unknown }).id))
        .map(item => normalizeUserEmployment(item, directories, complete))
}

// Строка графика: id — Employment UUID, user — вложенный объект, department/position — объекты.
export function normalizeScheduleRow(row: Schedule, directories: NameDirectories) {
    const source = row as unknown as Record<string, unknown>
    const employment = normalizeUserEmployment({
        id: row.id,
        department: source.department,
        position: source.position,
        hired_at: source.hired_at,
        fired_at: source.fired_at,
        salary_hour: source.salary_hour,
    }, directories, false)
    return { person: normalizePerson(row.user as unknown as Record<string, unknown>), employment }
}

// Данные GET User дополняют известное ТУ; имена из строки графика сохраняются, если справочника нет.
export function mergeEmployment(known: DossierEmployment | undefined, fresh: DossierEmployment): DossierEmployment {
    if (!known) return fresh
    return {
        ...fresh,
        departmentName: fresh.departmentName ?? known.departmentName,
        positionName: fresh.positionName ?? known.positionName,
    }
}

export const fullName = (person: DossierPerson) => [person.lastName, person.firstName, person.middleName].filter(Boolean).join(' ')
export const shortName = (person: DossierPerson) => {
    const initials = [person.firstName, person.middleName].filter(Boolean).map(part => `${part![0]}.`).join(' ')
    return [person.lastName, initials].filter(Boolean).join(' ')
}

// ---------- Форматирование ----------

export const DASH = '—'

// Часы — до одного десятичного знака, без лишнего нуля: 88,3 / 165.
export function formatHours(value: number | null | undefined) {
    if (value === null || value === undefined || !Number.isFinite(value)) return DASH
    return (Math.round(value * 10) / 10).toLocaleString('ru-RU', { maximumFractionDigits: 1 })
}

// Процент к норме: только при известной норме > 0 и известном значении (без NaN/Infinity).
export function percentOfNorm(value: number | null, norm: number | null) {
    if (value === null || norm === null || norm <= 0) return null
    return Math.round((value / norm) * 100)
}

// Длительность смены (часы, decimal) → «7:36».
export function formatDurationHours(hours: number | null) {
    if (hours === null || !Number.isFinite(hours) || hours < 0) return DASH
    const totalMinutes = Math.round(hours * 60)
    return `${Math.floor(totalMinutes / 60)}:${String(totalMinutes % 60).padStart(2, '0')}`
}

export const formatDate = (value: string | null | undefined) => (value ? dayjs(value).format('DD.MM.YYYY') : DASH)
export const formatRub = (value: number | null) => (value === null ? DASH : `${value.toLocaleString('ru-RU')} ₽`)
export const formatPercentage = (value: number | null) => (value === null ? DASH : `${Math.round(value * 100)} %`)

function plural(count: number, one: string, few: string, many: string) {
    const mod10 = count % 10
    const mod100 = count % 100
    if (mod10 === 1 && mod100 !== 11) return one
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
    return many
}

export const pluralShifts = (count: number) => `${count} ${plural(count, 'смена', 'смены', 'смен')}`
export const pluralDays = (count: number) => `${count} ${plural(count, 'день', 'дня', 'дней')}`
export const pluralEmployments = (count: number) => `${count} ${plural(count, 'трудоустройство', 'трудоустройства', 'трудоустройств')}`

// ---------- Активность и выбор отображаемого ТУ (r2) ----------

// Даты ТУ относительно текущего дня: начато — hired_at не позже сегодня (нет даты — считается начатым);
// завершено — fired_at (последний рабочий день, включительно) уже прошёл. Будущая дата увольнения
// не делает ТУ уволенным, будущая дата начала — не делает его действующим.
export const firedDayKey = (employment: DossierEmployment) => (employment.firedAt ? employment.firedAt.slice(0, 10) : null)
export const hiredDayKey = (employment: DossierEmployment) => (employment.hiredAt ? employment.hiredAt.slice(0, 10) : null)
export function isEndedEmployment(employment: DossierEmployment, todayKey: string) {
    const fired = firedDayKey(employment)
    return Boolean(fired && fired < todayKey)
}
export function isFutureEmployment(employment: DossierEmployment, todayKey: string) {
    const hired = hiredDayKey(employment)
    return !isEndedEmployment(employment, todayKey) && Boolean(hired && hired > todayKey)
}
export function isActiveEmployment(employment: DossierEmployment, todayKey: string) {
    return !isEndedEmployment(employment, todayKey) && !isFutureEmployment(employment, todayKey)
}

// Основное: среди действующих is_main=true (несколько — первое), иначе первое действующее в ответе.
// Если действующих нет, но есть будущие (принят, ещё не вышел) — тем же правилом среди будущих:
// это не архив. UI-правило r2; is_main не записывается.
export function pickMainEmployment(list: DossierEmployment[], todayKey: string) {
    const pick = (items: DossierEmployment[]) => items.find(item => item.isMain) ?? items[0] ?? null
    return pick(list.filter(item => isActiveEmployment(item, todayKey)))
        ?? pick(list.filter(item => isFutureEmployment(item, todayKey)))
}

// Архив: ТУ с последней датой увольнения, при равенстве — первое в ответе.
export function pickArchiveEmployment(list: DossierEmployment[]) {
    let result: DossierEmployment | null = null
    for (const item of list) {
        const fired = firedDayKey(item)
        if (fired && (!result || fired > (firedDayKey(result) ?? ''))) result = item
    }
    return result ?? list[0] ?? null
}

// Порядок списка ТУ: основное, действующие по дате начала, будущие по дате начала (равенство — порядок ответа),
// завершённые в конце.
export function sortEmploymentsForList(list: DossierEmployment[], mainId: string | null, todayKey: string) {
    const indexed = list.map((item, index) => ({ item, index }))
    const rank = (item: DossierEmployment) => {
        if (item.id === mainId) return 0
        if (isActiveEmployment(item, todayKey)) return 1
        return isFutureEmployment(item, todayKey) ? 2 : 3
    }
    return indexed.sort((a, b) => {
        const byRank = rank(a.item) - rank(b.item)
        if (byRank) return byRank
        if (rank(a.item) === 1 || rank(a.item) === 2) {
            const byHired = (hiredDayKey(a.item) ?? '').localeCompare(hiredDayKey(b.item) ?? '')
            if (byHired) return byHired
        }
        return a.index - b.index
    }).map(entry => entry.item)
}

// Стаж по известной дате начала выбранного ТУ (не дата первого найма в компанию).
export function formatTenure(hiredAt: string | null, until: Dayjs) {
    if (!hiredAt) return null
    const start = dayjs(hiredAt)
    if (!start.isValid() || until.isBefore(start, 'day')) return null
    const months = until.diff(start, 'month')
    if (months < 1) return 'меньше месяца'
    const years = Math.floor(months / 12)
    const rest = months % 12
    if (!years) return `${rest} мес`
    return rest ? `${years} ${plural(years, 'год', 'года', 'лет')} ${rest} мес` : `${years} ${plural(years, 'год', 'года', 'лет')}`
}

export const monthName = (date: Dayjs) => {
    const name = date.locale('ru').format('MMMM')
    return name.charAt(0).toUpperCase() + name.slice(1)
}
// «в ноябре»
export const monthPrepositional = (date: Dayjs) => {
    const forms = ['январе', 'феврале', 'марте', 'апреле', 'мае', 'июне', 'июле', 'августе', 'сентябре', 'октябре', 'ноябре', 'декабре']
    return `в ${forms[date.month()]}`
}

// ---------- Метрики ----------

// GET /api/shifts/employee-statistics/: body.<metric>.values[] {employment_id, value}.
// Отсутствующая метрика или значение — null, не 0 (метрики ограничиваются серверными settings).
export function pickMetric(body: unknown, metric: string, employmentId: string): number | null {
    if (!body || typeof body !== 'object') return null
    const values = (body as Record<string, { values?: unknown }>)[metric]?.values
    if (!Array.isArray(values)) return null
    const entry = values.find((item: { employment_id?: unknown }) => item?.employment_id === employmentId) as { value?: unknown } | undefined
    return entry ? numberOrNull(entry.value) : null
}

// ---------- Смены ----------

export const DATE_KEY = 'YYYY-MM-DD'

// duration — decimal string в часах, рассчитана сервером с обедом. Не заменяется разностью времён.
export function shiftDurationHours(shift: Partial<Shift>) {
    const value = numberOrNull(shift.duration)
    return value !== null && value >= 0 ? value : null
}

export interface ScheduleBody {
    id: string
    hoursNorm: number | null
    shifts: Shift[]
}

// GET /api/employments/{id}/schedule/ — один объект Schedule (не массив списка расписания).
export function normalizeScheduleBody(body: unknown): ScheduleBody {
    const value = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>
    return {
        id: String(value.id ?? ''),
        hoursNorm: numberOrNull(value.hours_norm),
        shifts: Array.isArray(value.shifts) ? (value.shifts as Shift[]) : [],
    }
}

export const shiftsByDate = (shifts: Shift[]) => {
    const map = new Map<string, Shift[]>()
    for (const shift of shifts) {
        if (!shift.date) continue
        const list = map.get(shift.date) ?? []
        list.push(shift)
        map.set(shift.date, list)
    }
    for (const list of map.values()) {
        list.sort((a, b) => String(a.plan_work_start_at ?? '').localeCompare(String(b.plan_work_start_at ?? '')))
    }
    return map
}

export const hasPlan = (shift: Partial<Shift>) => Boolean(shift.plan_work_start_at && shift.plan_work_end_at)

// ---------- Write payload ----------

export interface UserDraft {
    lastName: string
    firstName: string
    middleName: string
    phone: string
    email: string
    smartCheckin: boolean
}

export interface EmploymentDraft {
    position: Option | null
    hiredDate: Dayjs | null
    percentage: string
    shiftSchedule: Option | null
    salaryHour: string
    tabelCode: string
}

// PATCH /api/user/{User UUID}/ — плоский, только изменённые разрешённые поля.
// Нет вложенного user, read-only employments/address/id и mock-полей.
export interface UserPatch {
    first_name?: string
    last_name?: string
    middle_name?: string | null
    phone?: string
    email?: string | null
    smart_checkin_possible?: boolean
}

export function userDraftFrom(person: DossierPerson): UserDraft {
    return {
        lastName: person.lastName,
        firstName: person.firstName,
        middleName: person.middleName ?? '',
        phone: person.phone ?? '',
        email: person.email ?? '',
        smartCheckin: person.smartCheckin ?? false,
    }
}

export function buildUserPatch(base: DossierPerson, draft: UserDraft, allowed: { main: boolean, smart: boolean }): UserPatch {
    const patch: UserPatch = {}
    if (allowed.main) {
        if (draft.lastName !== base.lastName) patch.last_name = draft.lastName
        if (draft.firstName !== base.firstName) patch.first_name = draft.firstName
        if (draft.middleName !== (base.middleName ?? '')) patch.middle_name = draft.middleName || null
        if (draft.phone !== (base.phone ?? '')) patch.phone = draft.phone
        if (draft.email !== (base.email ?? '')) patch.email = draft.email || null
    }
    if (allowed.smart && base.smartCheckin !== null && draft.smartCheckin !== base.smartCheckin) {
        patch.smart_checkin_possible = draft.smartCheckin
    }
    return patch
}

// PATCH /api/employments/{Employment UUID}/ — только изменённые поля.
// percentage: UI 0..100 → доля 0..1 числом (прежний клиентский формат api-employment-edit, схема — decimal string).
// hired_at не отправляется null; salary_hour — целое, null из чтения не пересылается.
export interface EmploymentPatch {
    position?: string
    hired_at?: string
    percentage?: number
    shift_schedule?: string
    salary_hour?: number
    employee_tabel_code?: string
}

export function employmentDraftFrom(employment: DossierEmployment, options: { positions: Option[], shiftSchedules: Option[] }): EmploymentDraft {
    const find = (list: Option[], id: string | null) => (id ? list.find(item => item.id === id) ?? null : null)
    return {
        position: find(options.positions, employment.positionId)
            ?? (employment.positionId ? { id: employment.positionId, name: employment.positionName ?? DASH } : null),
        hiredDate: employment.hiredAt ? dayjs(employment.hiredAt) : null,
        percentage: employment.percentage === null ? '' : String(Math.round(employment.percentage * 100)),
        shiftSchedule: find(options.shiftSchedules, employment.shiftScheduleId),
        salaryHour: employment.salaryHour === null ? '' : String(employment.salaryHour),
        tabelCode: employment.tabelCode ?? '',
    }
}

export function buildEmploymentPatch(base: DossierEmployment, draft: EmploymentDraft): EmploymentPatch {
    const patch: EmploymentPatch = {}
    if (draft.position?.id && draft.position.id !== base.positionId) patch.position = draft.position.id
    const hiredAt = formatEmploymentDateForApi(draft.hiredDate)
    if (hiredAt && hiredAt !== formatEmploymentDateForApi(base.hiredAt)) patch.hired_at = hiredAt
    if (/^\d+$/.test(draft.percentage)) {
        const share = Number(draft.percentage) / 100
        if (base.percentage === null || Math.round(base.percentage * 100) !== Number(draft.percentage)) patch.percentage = share
    }
    if (draft.shiftSchedule?.id && draft.shiftSchedule.id !== base.shiftScheduleId) patch.shift_schedule = draft.shiftSchedule.id
    if (/^\d+$/.test(draft.salaryHour) && Number(draft.salaryHour) !== base.salaryHour) patch.salary_hour = Number(draft.salaryHour)
    // Непустой табельный код readonly; пустой можно заполнить.
    if (!base.tabelCode && draft.tabelCode.trim()) patch.employee_tabel_code = draft.tabelCode.trim()
    return patch
}

export const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9-]+(\.[A-Z0-9-]+)*\.[A-Z]{2,}$/i

export function validateUserDraft(draft: UserDraft) {
    const errors: Partial<Record<keyof UserDraft, string>> = {}
    if (draft.lastName.trim().length < 2) errors.lastName = 'Минимум 2 символа'
    if (draft.firstName.trim().length < 2) errors.firstName = 'Минимум 2 символа'
    if (!draft.phone.trim()) errors.phone = 'Укажите телефон'
    if (draft.email && !EMAIL_PATTERN.test(draft.email)) errors.email = 'Некорректный e-mail'
    return errors
}

export function validateEmploymentDraft(draft: EmploymentDraft, base: DossierEmployment) {
    const errors: Partial<Record<keyof EmploymentDraft, string>> = {}
    if (!draft.position?.id) errors.position = 'Выберите должность'
    if (!draft.hiredDate || !dayjs(draft.hiredDate).isValid()) errors.hiredDate = 'Укажите дату'
    // fired_at — последний рабочий день: начало не позже него.
    else if (base.firedAt && dayjs(draft.hiredDate).isAfter(dayjs(base.firedAt.slice(0, 10)), 'day')) errors.hiredDate = 'Позже даты окончания'
    if (!/^\d+$/.test(draft.percentage) || Number(draft.percentage) > 100) errors.percentage = 'Целое от 0 до 100'
    if (!draft.shiftSchedule?.id) errors.shiftSchedule = 'Выберите доступность'
    if (draft.salaryHour === '' ? base.salaryHour !== null : !/^\d+$/.test(draft.salaryHour)) errors.salaryHour = 'Целое число'
    return errors
}

// Сообщение ошибки write: 400 — тело валидации, иначе общий текст.
export function describeWriteError(error: unknown, fallback: string) {
    const response = (error as { response?: { status?: number, data?: unknown } })?.response
    const data = response?.data as Record<string, unknown> | undefined
    if (!response) return `${fallback}: нет связи с сервером`
    if (response.status === 404) return `${fallback}: запись недоступна или удалена`
    if (typeof data?.msg === 'string' && data.msg) return data.msg
    const body = (data?.body ?? data) as Record<string, unknown> | undefined
    if (body && typeof body === 'object') {
        const messages = Object.entries(body)
            .filter(([key]) => key !== 'status')
            .flatMap(([, value]) => (Array.isArray(value) ? value : [value]))
            .filter((value): value is string => typeof value === 'string')
        if (messages.length) return messages.join(' ')
    }
    return fallback
}

// 400 {body: {field: [message]}} → сообщения по полям API (серверный отказ закреплённого поля и т. п.).
export function fieldErrorsOf(error: unknown): Record<string, string> {
    const response = (error as { response?: { status?: number, data?: unknown } })?.response
    if (response?.status !== 400) return {}
    const data = response.data as Record<string, unknown> | undefined
    const body = data?.body
    if (!body || typeof body !== 'object' || Array.isArray(body)) return {}
    const result: Record<string, string> = {}
    for (const [key, value] of Object.entries(body as Record<string, unknown>)) {
        const messages = (Array.isArray(value) ? value : [value]).filter((item): item is string => typeof item === 'string')
        if (messages.length) result[key] = messages.join(' ')
    }
    return result
}

// ---------- Поля из 1С (public_api_fields) ----------

export const ONE_C_LOCK_TITLE = 'Из 1С — меняется в 1С'
export const LOCKS_UNKNOWN_TITLE = 'Не удалось проверить поля из 1С'

// Закреплено интеграцией: имя поля в массиве. Неизвестный массив (null) запрещает запись всего объекта.
export const isApiLocked = (fields: string[] | null, name: string) => fields === null || fields.includes(name)

// Удаляет из changed-only payload закреплённые поля (их нельзя менять с фронта).
export function omitLocked<T extends object>(patch: T, fields: string[] | null): T {
    if (fields === null) return {} as T
    const result = { ...patch }
    for (const key of Object.keys(result)) {
        if (fields.includes(key)) delete (result as Record<string, unknown>)[key]
    }
    return result
}

// ---------- Новое ТУ: POST /api/employments/ (правила прежнего EmploymentsCreate) ----------

export interface NewEmploymentDraft {
    department: Option | null
    position: Option | null
    hiredDate: Dayjs | null
    percentage: string
    shiftSchedule: Option | null
    salaryHour: string
    tabelCode: string
}

export const newEmploymentDraft = (): NewEmploymentDraft => ({
    department: null,
    position: null,
    hiredDate: null,
    percentage: '100',
    shiftSchedule: null,
    salaryHour: '0',
    tabelCode: '',
})

export function validateNewEmployment(draft: NewEmploymentDraft) {
    const errors: Partial<Record<keyof NewEmploymentDraft, string>> = {}
    if (!draft.department?.id) errors.department = 'Выберите подразделение'
    if (!draft.position?.id) errors.position = 'Выберите должность'
    if (!draft.hiredDate || !dayjs(draft.hiredDate).isValid()) errors.hiredDate = 'Укажите дату'
    if (!/^\d+$/.test(draft.percentage) || Number(draft.percentage) > 100) errors.percentage = 'Целое от 0 до 100'
    if (!draft.shiftSchedule?.id) errors.shiftSchedule = 'Выберите доступность'
    if (!/^\d+$/.test(draft.salaryHour)) errors.salaryHour = 'Целое число'
    return errors
}

// user/department/position/hired_at обязательны; fired_at при создании не задаётся; is_main не отправляется.
export function buildCreateEmploymentPayload(userId: string, draft: NewEmploymentDraft) {
    const payload: Record<string, string | number> = {
        user: userId,
        department: draft.department!.id,
        position: draft.position!.id,
        hired_at: formatEmploymentDateForApi(draft.hiredDate)!,
        percentage: Number(draft.percentage) / 100,
        shift_schedule: draft.shiftSchedule!.id,
        salary_hour: Number(draft.salaryHour),
    }
    if (draft.tabelCode.trim()) payload.employee_tabel_code = draft.tabelCode.trim()
    return payload
}
